/**
 * The session, as the rest of the application sees it.
 *
 * `GET /auth/me` is the first call of every screen (KLQ-20260819-001, "Session wiring") and
 * the only source of who the caller is. It is a TanStack Query rather than a store because
 * it is server state: the server can end a session without telling the tab, and a cached
 * copy that outlives the cookie is exactly the bug a store would introduce.
 *
 * Nothing here retries. The 401 retry — one `POST /auth/refresh`, one replay — lives in
 * `api/client.ts` so that *every* authenticated call gets it, not just this one; by the time
 * a 401 reaches this layer the rotation has already been tried and failed, and the only
 * remaining answer is the sign-in screen.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

export type Me = components['schemas']['MeResponse'];

/** Everything derived from the session hangs off this prefix, so one removal clears it all. */
export const SESSION_QUERY_KEY = ['session'] as const;
const ME_QUERY_KEY = [...SESSION_QUERY_KEY, 'me'] as const;

export const meQueryOptions = queryOptions({
  queryKey: ME_QUERY_KEY,
  queryFn: () => unwrap(api.GET('/api/v1/auth/me')),
  // A session that has just been established is worth re-reading; one that has not changed
  // in five minutes is not. Nothing in Kalqa is realtime (CLAUDE.md, data-fetching rules).
  staleTime: 5 * 60_000,
  // See the module note: the retry that could help already happened one layer down.
  retry: false,
});

export function useMe() {
  return useQuery(meQueryOptions);
}

/**
 * Whether this session should be routed straight into B0 (`/setup`, KLQ-20260819-002).
 *
 * **Two conditions, and the second one is the whole point.** An unclaimed home workspace is
 * not on its own a reason to force somebody through company setup: an invited engineer from
 * another firm also has an unclaimed home workspace, because acceptance creates one for them
 * (`MCK-20260812-009` §A). Sending *them* to B0 would open the product by demanding they name
 * a company they did not come here to create, before they can reach the project they were
 * invited to. So the guest shape — a session with projects — is never forced; they reach B0
 * through the explicit affordance on the home screen, which is what §B's triggers describe.
 *
 * `project_ids` is emptiness-tested and nothing more. It is a hint the server sends so the
 * client knows what to offer (`MeResponse`, "not as an authorisation input"), and this is a
 * routing decision, not an authorisation one — the claim endpoint decides who may claim.
 *
 * **Three conditions since `KLQ-20260819-007`.** The third is the one multi-membership added:
 * somebody who already works for a studio has a company, whatever the state of the workspace
 * their own account was born into. Sending them to B0 would demand they found a second firm
 * before reaching the one that hired them — the same mistake the guest condition above
 * exists to prevent, arriving through a different door. It asks about the **home** workspace
 * specifically and then about the rest, rather than about the set as a whole, because
 * `home_workspace_claimed` keeps its meaning: it is a fact about home and about nothing else.
 */
export function needsWorkspaceSetup(me: Me): boolean {
  if (me.home_workspace_claimed) return false;
  if (me.project_ids.length > 0) return false;
  return !me.workspaces.some((workspace) => !workspace.is_home && workspace.claimed);
}

/** `true` when the failure is "we do not know who you are", rather than any other failure. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * Sign out, then forget everything the session made visible.
 *
 * `clear()` and not `invalidateQueries()`: invalidation refetches, which for a signed-out
 * tab means a burst of 401s, and leaves the previous user's data on screen while it happens.
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/auth/sign-out')),
    onSettled: () => {
      queryClient.clear();
    },
  });
}
